import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useAuth } from "../context/AuthContext";
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  AccountBalance as VATIcon,
  ArrowBack as BackIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import ReportDataGrid from "../components/ReportDataGrid";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/apiService";
import riyalIcon from "../assets/riyal.png";
import { formatDateTime } from "../utils/dateUtils";
import { useTranslation } from "../hooks/useTranslation";

const DSRReport = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isVatAdmin = currentUser?.role === "vatadmin";
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const { t, language } = useTranslation();
  const isRtl = language === "ar";
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [filters, setFilters] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });

  const fetchReport = async () => {
    try {
      setLoading(true);

      const params = {};
      if (filters.startDate) {
        params.startDate = filters.startDate.toISOString().split("T")[0];
      }
      if (filters.endDate) {
        params.endDate = filters.endDate.toISOString().split("T")[0];
      }

      if (language === "ar" || language === "en") {
        params.language = language;
      }

      if (isVatAdmin && selectedCompany !== "all") {
        params.tenantIds = selectedCompany;
      }

      const response = await api.get("/reports/dsr", { params });

      if (response.data && response.data.success && response.data.data) {
        const apiData = response.data.data;
        const invoicesWithId = (apiData.invoices || []).map(
          (invoice, index) => ({
            ...invoice,
            id: invoice.invoiceNo || index, // Use invoiceNo as ID, fallback to index
          }),
        );

        setReportData(invoicesWithId);
        setTotals(apiData.totals || null);
      } else {
        console.error("API Response failed or no data:", response);
      }
    } catch (err) {
      console.error("Error fetching DSR report:", err);
      alert(t("Reports.Failed_To_Fetch_DSR"));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get("/vat-admin/my-companies", {
        params: { limit: "all" },
      });

      const records = res.data?.data ?? res.data ?? [];
      setCompanies(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  };

  useEffect(() => {
    if (isVatAdmin) {
      fetchCompanies();
    }

    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  // Define columns for the data grid
  const columns = [
    {
      field: "invoiceNo",
      headerName: t("Reports.Invoice_No"),
      flex: 1,
      minWidth: 130,
    },
    {
      field: "invoiceDate",
      headerName: t("Reports.Date_Time"),
      flex: 1.2,
      minWidth: 150,
      valueFormatter: ({ value }) => formatDateTime(value),
    },
    {
      field: "customerName",
      headerName: t("Reports.Customer"),
      flex: 1.5,
      minWidth: 180,
    },
    {
      field: "customerType",
      headerName: t("Reports.Type"),
      flex: 0.6,
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "B2B" ? "primary" : "secondary"}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: "subTotal",
      headerName: t("Reports.Sub_Total"),
      flex: 1,
      minWidth: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Box
            component="img"
            src={riyalIcon}
            alt="SAR"
            sx={{
              width: { xs: 12, sm: 14 },
              height: "auto",
              objectFit: "contain",
            }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
    },
    {
      field: "vatAmt",
      headerName: t("Reports.VAT"),
      flex: 0.8,
      minWidth: 100,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Box
            component="img"
            src={riyalIcon}
            alt="SAR"
            sx={{
              width: { xs: 12, sm: 14 },
              height: "auto",
              objectFit: "contain",
            }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
    },
    {
      field: "totalAmt",
      headerName: t("Reports.Total"),
      flex: 1,
      minWidth: 130,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Box
            component="img"
            src={riyalIcon}
            alt="SAR"
            sx={{
              width: { xs: 12, sm: 14 },
              height: "auto",
              objectFit: "contain",
            }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
      cellClassName: "font-weight-bold",
    },
    {
      field: "paidAmt",
      headerName: t("Reports.Paid"),
      flex: 0.9,
      minWidth: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Box
            component="img"
            src={riyalIcon}
            alt="SAR"
            sx={{
              width: { xs: 12, sm: 14 },
              height: "auto",
              objectFit: "contain",
            }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
    },
    {
      field: "balanceAmt",
      headerName: t("Reports.Balance"),
      flex: 0.9,
      minWidth: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Box
            component="img"
            src={riyalIcon}
            alt="SAR"
            sx={{
              width: { xs: 12, sm: 14 },
              height: "auto",
              objectFit: "contain",
            }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
      cellClassName: (params) => (params.value > 0 ? "text-error" : ""),
    },
    {
      field: "paymentStatus",
      headerName: t("Reports.Status"),
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => {
        const val = params.value?.toLowerCase();
        const label =
          val === "paid"
            ? t("Reports.Paid")
            : val === "pending"
              ? t("Reports.Pending")
              : params.value?.toUpperCase() || t("Reports.NA");
        return (
          <Chip
            label={label}
            size="small"
            color={
              val === "paid"
                ? "success"
                : val === "pending"
                  ? "warning"
                  : "default"
            }
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
  ];

  return (
    <Container
      maxWidth={false}
      sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}
    >
      {/* Header */}
      <Box sx={{ mb: 0.5, display: "flex", alignItems: "center" }}>
        <IconButton
          onClick={() => navigate("/reports")}
          size="small"
          sx={{ mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }}
        >
          <BackIcon sx={{ transform: isRtl ? "rotate(180deg)" : "none" }} />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {t("Reports.Daily_Sales_Report_Title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Reports.DSR_Analysis_Insights")}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 0.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={isVatAdmin ? 3 : 4}>
              <DatePicker
                label={t("Reports.From_Date")}
                value={filters.startDate}
                onChange={(newValue) =>
                  handleFilterChange("startDate", newValue)
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                      },
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={isVatAdmin ? 3 : 4}>
              <DatePicker
                label={t("Reports.To_Date")}
                value={filters.endDate}
                minDate={filters.startDate || undefined}
                onChange={(newValue) => handleFilterChange("endDate", newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                      },
                    },
                  },
                }}
              />
            </Grid>

            {isVatAdmin && (
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("Common.Company_Name") || "Company"}
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                    },
                  }}
                >
                  <MenuItem value="all">
                    {t("Common.All_Companies") || "All Companies"}
                  </MenuItem>

                  {companies.map((company) => (
                    <MenuItem key={company.tenantId} value={company.tenantId}>
                      {company.name || company.tenantId}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} md={isVatAdmin ? 3 : 4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                startIcon={
                  <AssessmentIcon
                    sx={{
                      ml: isRtl ? 1 : 0,
                      mr: isRtl ? 0 : 1,
                    }}
                  />
                }
              >
                {loading ? t("Reports.Loading") : t("Reports.Generate_Report")}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {/* Summary Cards */}
      {totals && (
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0,
                borderLeft: isRtl ? "none" : "4px solid #4361ee",
                borderRight: isRtl ? "4px solid #4361ee" : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#4361ee15",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: isRtl ? 0 : 1.5,
                    ml: isRtl ? 1.5 : 0,
                  }}
                >
                  <MoneyIcon sx={{ color: "#4361ee", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Reports.Total_Sales")}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <Box
                      component="img"
                      src={riyalIcon}
                      alt="SAR"
                      sx={{
                        width: { xs: 16, md: 20 },
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                    {totals.totalSales?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) || "0.00"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0,
                borderLeft: isRtl ? "none" : "4px solid #2e7d32",
                borderRight: isRtl ? "4px solid #2e7d32" : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#2e7d3215",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: isRtl ? 0 : 1.5,
                    ml: isRtl ? 1.5 : 0,
                  }}
                >
                  <ReceiptIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Reports.Cash_Sales")}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <Box
                      component="img"
                      src={riyalIcon}
                      alt="SAR"
                      sx={{
                        width: { xs: 16, md: 20 },
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                    {totals.cashSales?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) || "0.00"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0,
                borderLeft: isRtl ? "none" : "4px solid #ed6c02",
                borderRight: isRtl ? "4px solid #ed6c02" : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#ed6c0215",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: isRtl ? 0 : 1.5,
                    ml: isRtl ? 1.5 : 0,
                  }}
                >
                  <TrendingUpIcon sx={{ color: "#ed6c02", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Reports.Credit_Sales")}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <Box
                      component="img"
                      src={riyalIcon}
                      alt="SAR"
                      sx={{
                        width: { xs: 16, md: 20 },
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                    {totals.creditSales?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) || "0.00"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0,
                borderLeft: isRtl ? "none" : "4px solid #d32f2f",
                borderRight: isRtl ? "4px solid #d32f2f" : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#d32f2f15",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: isRtl ? 0 : 1.5,
                    ml: isRtl ? 1.5 : 0,
                  }}
                >
                  <VATIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Reports.VAT_Amount")}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <Box
                      component="img"
                      src={riyalIcon}
                      alt="SAR"
                      sx={{
                        width: { xs: 16, md: 20 },
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                    {totals.totalVAT?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) || "0.00"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Data Grid */}
      <ReportDataGrid
        title={t("Reports.Daily_Sales_Report")}
        data={reportData}
        columns={columns}
        loading={loading}
        onRefresh={handleApplyFilters}
        exportFileName={`dsr-report-${new Date().toISOString().split("T")[0]}`}
        pageSize={25}
        height={700}
        getRowId={(row) =>
          row.id || row.invoiceNumber || Math.random().toString()
        }
        totals={totals}
        dateRange={{ startDate: filters.startDate, endDate: filters.endDate }}
      />
    </Container>
  );
};

export default DSRReport;
